import React, { useState, useRef, useEffect, forwardRef } from "react";
import {
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { IoImageOutline } from "react-icons/io5";
import { renderCurrencyIconColored } from "../../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../../utils/CommonFunction";
import SupplyApplyPills from "../../../components/SupplyApplyPills";

const NumberInput = forwardRef(
  (
    { value, onChange, width = 80, placeholder = "", hasError = false, onEnter, disabled = false },
    ref
  ) => (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      autoComplete="off"
      placeholder={placeholder}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => {
        if (disabled) return;
        const v = e.target.value;
        if (v === "" || /^\d+$/.test(v)) onChange(v);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter") {
          e.preventDefault();
          onEnter?.();
        }
      }}
      style={{
        width,
        height: 32,
        border: hasError ? "1px solid #EF4444" : "1px solid #D1D5DB",
        borderRadius: 6,
        padding: "4px 8px",
        fontSize: 13,
        color: disabled ? "#9ca3af" : "#0d0d0e",
        backgroundColor: disabled ? "#f3f4f6" : "#fff",
        outline: "none",
        fontFamily: "Inter Tight",
        cursor: disabled ? "not-allowed" : "text",
      }}
    />
  )
);

const ExternallyProcuredBadge = ({ procurement }) => {
  const label =
    procurement === "User" ? "Supplied by Customer" : "Supplied by Reno";
  return (
    <span
      title="You don't need to price this — material is provided externally"
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 8px",
        fontSize: 11,
        fontWeight: 500,
        color: "#374151",
        backgroundColor: "#f3f4f6",
        border: "1px solid #d1d5db",
        borderRadius: 6,
      }}
    >
      {label}
    </span>
  );
};

const CurrencyValue = ({ amount, size = 12, weight = 500, dashOnZero = false }) => {
  const isEmpty = amount == null || amount === "";
  const isZero = !isEmpty && Number(amount) === 0;
  if (isEmpty || (dashOnZero && isZero)) {
    return <span style={{ fontWeight: weight, fontSize: size }}>-</span>;
  }
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontWeight: weight,
        fontSize: size,
        color: "#0d0d0e",
      }}
    >
      {renderCurrencyIconColored("black-small")}
      {formattedAmountWithoutAed(amount)}
    </span>
  );
};

const RfqBoqItemRow = ({
  boq,
  boqPriceInputs,
  isBOQPriceLoading,
  isDisabled = false,
  onSave,
  onChange,
  roomName,
  autoFocus = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [errors, setErrors] = useState({ qty: false, price: false });
  const priceInputRef = useRef(null);
  const qtyInputRef = useRef(null);

  const isActivities = boq.type === "Activities";
  const externallyProcured =
    boq.procurement === "User" || boq.procurement === "Reno";
  const boqImage = boq.image && boq.image.length > 0 ? boq.image[0] : null;

  const priceKey = boq.id;
  const qtyKey = isActivities ? `${boq.id}_days` : `${boq.id}_quantity`;

  const savedPriceRaw = isActivities ? boq.manpower_rate : boq.unit_cost;
  const savedQtyRaw = isActivities ? boq.days : boq.quantity;

  const priceValue =
    boqPriceInputs?.[priceKey] !== undefined
      ? boqPriceInputs[priceKey]
      : savedPriceRaw ?? "";

  const qtyValue =
    boqPriceInputs?.[qtyKey] !== undefined
      ? boqPriceInputs[qtyKey]
      : savedQtyRaw ?? "";

  // A saved price of 0 counts as priced, so the CTA reads "Update" rather than
  // offering "Save" again on a row that is already filled in.
  const hasExistingPrice =
    savedPriceRaw != null &&
    savedPriceRaw !== "" &&
    Number.isFinite(Number(savedPriceRaw)) &&
    Number(savedPriceRaw) >= 0;
  const saveLabel = hasExistingPrice ? "Update" : "Save";

  useEffect(() => {
    if (autoFocus) {
      priceInputRef.current?.focus();
    }
  }, [autoFocus]);

  const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

  const handleInputChange = (key, val) => {
    if (key === priceKey && errors.price) {
      setErrors((e) => ({ ...e, price: false }));
    }
    if (key === qtyKey && errors.qty) {
      setErrors((e) => ({ ...e, qty: false }));
    }
    onChange?.(key, val);
  };

  const attemptSave = (origin) => {
    const priceFilled = isFilled(priceValue);
    const qtyFilled = isFilled(qtyValue);

    if (priceFilled && qtyFilled) {
      setErrors({ qty: false, price: false });
      onSave?.(boq.id);
      return;
    }

    if (origin === "price" && priceFilled && !qtyFilled) {
      setErrors({ qty: true, price: false });
      qtyInputRef.current?.focus();
      return;
    }
    if (origin === "qty" && qtyFilled && !priceFilled) {
      setErrors({ qty: false, price: true });
      priceInputRef.current?.focus();
      return;
    }

    setErrors({ qty: !qtyFilled, price: !priceFilled });
    if (!priceFilled) priceInputRef.current?.focus();
    else if (!qtyFilled) qtyInputRef.current?.focus();
  };

  const leadTimeText =
    boq.lead_time != null && String(boq.lead_time).trim() !== ""
      ? String(boq.lead_time).trim()
      : "";

  const specText =
    boq.specification != null && String(boq.specification).trim() !== ""
      ? String(boq.specification).trim()
      : "";

  const savedPrice = isActivities ? boq.manpower_rate : boq.unit_cost;
  const isPriceMissing = savedPrice == null || savedPrice === "";
  const missingBorder = isPriceMissing ? "1px solid #EF4444" : "1px solid #e5e7eb";

  const ImageBox = ({ size = 88, minHeight = 112 }) => (
    <div style={{ width: size, flexShrink: 0, alignSelf: "stretch" }}>
      <div
        style={{
          position: "relative",
          height: "100%",
          minHeight,
          maxHeight: minHeight,
          width: "100%",
          overflow: "hidden",
          borderRadius: 8,
          backgroundColor: "#e5e7eb",
        }}
      >
        {boqImage?.image ? (
          <>
            <img
              src={boqImage.image}
              alt={boq.item_name}
              style={{
                height: "100%",
                width: "100%",
                minHeight,
                objectFit: "cover",
                cursor: "pointer",
              }}
              onClick={() => setLightboxOpen(true)}
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
            {lightboxOpen && (
              <div
                onClick={() => setLightboxOpen(false)}
                style={{
                  position: "fixed",
                  inset: 0,
                  backgroundColor: "rgba(0,0,0,0.8)",
                  zIndex: 9999,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={boqImage.image}
                  alt=""
                  style={{ maxWidth: "90%", maxHeight: "90%" }}
                />
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              display: "flex",
              height: "100%",
              minHeight,
              width: "100%",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <IoImageOutline style={{ width: 20, height: 20, color: "#9ca3af" }} />
          </div>
        )}
      </div>
    </div>
  );

  const SaveButton = (
    <button
      onClick={() => attemptSave("button")}
      disabled={isBOQPriceLoading}
      style={{
        padding: "4px 12px",
        fontSize: 12,
        fontWeight: 500,
        color: "#fff",
        backgroundColor: "#4D6BF6",
        border: "none",
        borderRadius: 8,
        cursor: isBOQPriceLoading ? "not-allowed" : "pointer",
        opacity: isBOQPriceLoading ? 0.6 : 1,
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {isBOQPriceLoading ? (
        <CircularProgress size={12} sx={{ color: "#fff" }} />
      ) : (
        saveLabel
      )}
    </button>
  );

  // Externally-procured BOQs (User/Reno) are not contractor-quotable. The contractor sees
  // them for scope context but can't price them; backend forces quoted_price=0 either way.
  const showActions = !isDisabled && !externallyProcured;

  if (isMobile) {
    return (
      <div
        style={{
          position: "relative",
          backgroundColor: "#fff",
          padding: 16,
          borderRadius: 6,
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          border: missingBorder,
        }}
      >
        {/* Header: image + name + supply label */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 8 }}>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 6,
                overflow: "hidden",
                flexShrink: 0,
                backgroundColor: "#f3f4f6",
              }}
            >
              {boqImage?.image ? (
                <img
                  src={boqImage.image}
                  alt={boq.item_name}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <IoImageOutline style={{ width: 12, height: 12, color: "#9ca3af" }} />
                </div>
              )}
            </div>
            <span
              style={{
                fontSize: 14,
                color: "#0D0D0E",
                fontWeight: 500,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {boq.item_name || "-"}
              {roomName ? ` (${roomName})` : ""}
            </span>
            {externallyProcured && (
              <ExternallyProcuredBadge procurement={boq.procurement} />
            )}
          </div>
          {boq.supply_type && <SupplyApplyPills supplyType={boq.supply_type} />}
        </div>

        {/* BOQ Type / Material Unit / Quantity */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA" }}>BOQ Type</span>
            <span>
              <span
                style={{
                  fontSize: 12,
                  color: "#1e40af",
                  backgroundColor: "#dbeafe",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                {boq.type || "Materials"}
              </span>
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA" }}>Material Unit</span>
            <span style={{ fontSize: 14, color: "#0D0D0E" }}>
              {boq.unit_of_measurement || "-"}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>
              {isActivities ? "Days" : "Quantity"}
            </span>
            <NumberInput
              ref={qtyInputRef}
              value={qtyValue}
              onChange={(v) => handleInputChange(qtyKey, v)}
              width="100%"
              placeholder={isActivities ? "Days" : "Quantity"}
              hasError={errors.qty}
              onEnter={() => attemptSave("qty")}
              disabled={externallyProcured}
            />
          </div>
        </div>

        {/* Unit Cost + Total Cost */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>
              {isActivities ? "Manpower Rate" : "Unit Cost"}
            </span>
            <NumberInput
              ref={priceInputRef}
              value={priceValue}
              onChange={(v) => handleInputChange(priceKey, v)}
              width="100%"
              placeholder="Rate"
              hasError={errors.price}
              onEnter={() => attemptSave("price")}
              disabled={externallyProcured}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>Total Cost</span>
            <CurrencyValue amount={boq.total_cost} size={14} dashOnZero={isPriceMissing} />
          </div>
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ fontSize: 14, color: "#949DAA", marginBottom: 4 }}>Lead Time</span>
            <span style={{ fontSize: 14, color: "#0D0D0E" }} title={leadTimeText || undefined}>
              {leadTimeText
                ? `${leadTimeText} day${leadTimeText === "1" ? "" : "s"}`
                : "-"}
            </span>
          </div>
        </div>

        {/* Specification */}
        <div style={{ display: "flex", minWidth: 0, width: "100%", flexDirection: "column" }}>
          <span style={{ marginBottom: 4, fontSize: 14, color: "#949DAA" }}>Specification</span>
          <span
            style={{
              display: "block",
              width: "100%",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: 14,
              color: "#0D0D0E",
            }}
            title={specText || undefined}
          >
            {specText || "—"}
          </span>
        </div>

        {showActions && (
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, paddingTop: 4 }}>
            {SaveButton}
          </div>
        )}
      </div>
    );
  }

  // Desktop — row layout
  return (
    <div
      style={{
        position: "relative",
        backgroundColor: "#fff",
        borderRadius: 8,
        border: missingBorder,
        padding: 16,
        marginBottom: 8,
      }}
    >
      <div
        style={{
          display: "flex",
          minWidth: 0,
          flexDirection: "row",
          alignItems: "stretch",
          gap: 16,
          fontSize: 12,
        }}
      >
        <ImageBox />

        {/* Middle: 3 rows */}
        <div
          style={{ display: "flex", minWidth: 0, flex: 1, flexDirection: "column", gap: 10 }}
        >
          {/* Row 1: name/supply | Material Unit | BOQ Type | Quantity */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <div style={{ minWidth: 0, paddingRight: 4 }}>
              <div
                style={{
                  fontWeight: 500,
                  color: "#111827",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                }}
                title={boq.item_name}
              >
                {boq.item_name}
                {roomName ? ` (${roomName})` : ""}
              </div>
              {boq.supply_type && (
                <div style={{ marginTop: 4 }}>
                  <SupplyApplyPills supplyType={boq.supply_type} />
                </div>
              )}
              {externallyProcured && (
                <div style={{ marginTop: 4 }}>
                  <ExternallyProcuredBadge procurement={boq.procurement} />
                </div>
              )}
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>Material Unit</div>
              <div style={{ fontWeight: 500 }}>{boq.unit_of_measurement || "-"}</div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280", marginBottom: 2 }}>BOQ Type</div>
              <span
                style={{
                  fontSize: 12,
                  color: "#1e40af",
                  backgroundColor: "#dbeafe",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                {boq.type || "Materials"}
              </span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>
                {isActivities ? "Days" : "Quantity"}
              </div>
              <div style={{ marginTop: 2 }}>
                <NumberInput
                  ref={qtyInputRef}
                  value={qtyValue}
                  onChange={(v) => handleInputChange(qtyKey, v)}
                  width={90}
                  hasError={errors.qty}
                  onEnter={() => attemptSave("qty")}
                  placeholder={isActivities ? "Days" : "Qty"}
                  disabled={externallyProcured}
                />
              </div>
            </div>
          </div>

          {/* Row 2: Unit Cost | Total Cost | Lead Time | (spare) */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>
                {isActivities ? "Manpower Rate" : "Unit Cost"}
              </div>
              <div style={{ marginTop: 2 }}>
                <NumberInput
                  ref={priceInputRef}
                  value={priceValue}
                  onChange={(v) => handleInputChange(priceKey, v)}
                  width={110}
                  hasError={errors.price}
                  onEnter={() => attemptSave("price")}
                  placeholder="Rate"
                  disabled={externallyProcured}
                />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>Total Cost</div>
              <div style={{ fontWeight: 500 }}>
                <CurrencyValue amount={boq.total_cost} dashOnZero={isPriceMissing} />
              </div>
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: "#6b7280" }}>Lead Time</div>
              <div style={{ fontWeight: 500 }} title={leadTimeText || undefined}>
                {leadTimeText
                  ? `${leadTimeText} day${leadTimeText === "1" ? "" : "s"}`
                  : "-"}
              </div>
            </div>
          </div>

          {/* Row 3: Specification */}
          <div style={{ minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 500, color: "#6b7280" }}>
              Specification
            </span>
            <p
              style={{
                margin: 0,
                marginTop: 2,
                fontSize: 12,
                color: "#111827",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={specText || undefined}
            >
              {specText || "—"}
            </p>
          </div>
        </div>

        {/* Actions */}
        {showActions && (
          <div
            style={{
              display: "flex",
              width: 110,
              flexShrink: 0,
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              alignSelf: "stretch",
            }}
          >
            {SaveButton}
          </div>
        )}
      </div>
    </div>
  );
};

export default RfqBoqItemRow;
